/**
 * Image Setup
 *
 * Initializes image picker components and wires handlers to the page.
 */

import { ImageListener } from "./image-listener";
import { ImageHandler } from "./image-handler";
import { UISymbols } from "../../../constants/ui-symbols";
import { Roles } from "../../../constants/roles";
import type { ExistingImageData } from "./image-handler";
import { imageForm } from "./image-form";

interface ImageSetupOptions {
    maxFiles?: number;
    maxSize?: string | number;
    acceptedTypes?: string | string[];
    existingImages?: ExistingImageData[];
}

const imageHandlerRegistry = new Map<string, ImageHandler>();

function toPostMediaKey(postKey: string | number): string {
    return String(postKey);
}

function toExistingImageId(rawValue: string | undefined, fallbackIndex: number): number {
    const normalizedId = Number(rawValue);
    return Number.isInteger(normalizedId) && normalizedId >= 0 ? normalizedId : fallbackIndex;
}

function registerImageHandler(postKey: string, handler: ImageHandler): void {
    imageHandlerRegistry.set(toPostMediaKey(postKey), handler);
}

function getImageHandler(postKey: string): ImageHandler | undefined {
    return imageHandlerRegistry.get(toPostMediaKey(postKey));
}

function getImageHandlerByPostId(postId: number): ImageHandler | undefined {
    return imageHandlerRegistry.get(toPostMediaKey(postId));
}

class ImageSetup {
    private static resolveContainer(handler: ImageHandler, root: HTMLElement | null = null): HTMLElement | null {
        if (root instanceof HTMLElement && root.getAttribute("data-role") === Roles.IMAGE_MANAGER) {
            return root;
        }

        const scoped = root ? imageForm.getContainer(handler.postKey, root) : null;
        if (scoped instanceof HTMLElement) {
            return scoped;
        }

        const global = imageForm.getContainer(handler.postKey, document);
        return global instanceof HTMLElement ? global : null;
    }

    public static applyStaticControlSymbols(container: HTMLElement | null): void {
        if (!(container instanceof HTMLElement)) return;

        imageForm.getRemoveButtons(container).forEach((button) => {
            if (button instanceof HTMLButtonElement) {
                button.textContent = UISymbols.PICKER_REMOVE;
            }
        });
    }

    public static initializeCreateMode(
        postKey: string,
        root: HTMLElement | null = null,
        options: ImageSetupOptions = {},
    ): ImageHandler | null {
        const handler = new ImageHandler(postKey, "create", options);
        const container = ImageSetup.resolveContainer(handler, root);
        if (!(container instanceof HTMLElement)) return null;

        const fileInput = imageForm.getFileInput(container);
        const addBtn = imageForm.getAddButton(container);
        const imageGrid = imageForm.getImageGrid(container);
        const dropHint = imageForm.getDropHint(container);

        if (!(fileInput instanceof HTMLInputElement)) return null;
        if (!(addBtn instanceof HTMLButtonElement)) return null;
        if (!(imageGrid instanceof HTMLElement)) return null;

        handler.container = container;
        handler.fileInput = fileInput;
        handler.addBtn = addBtn;
        handler.imageGrid = imageGrid;
        handler.dropHint = dropHint instanceof HTMLElement ? dropHint : imageGrid;

        ImageSetup.applyStaticControlSymbols(handler.container);
        ImageListener.setupFileInputListener(handler);
        ImageListener.setupCreateModeClickListeners(handler);
        ImageListener.setupDragAndDrop(handler, handler.dropHint);
        registerImageHandler(handler.postKey, handler);
        return handler;
    }

    public static initializeEditMode(
        postId: number,
        root: HTMLElement | null = null,
        options: ImageSetupOptions = {},
    ): ImageHandler | null {
        const postKey = toPostMediaKey(postId);
        const handler = new ImageHandler(postKey, "edit", options);
        const container = ImageSetup.resolveContainer(handler, root);
        if (!(container instanceof HTMLElement)) return null;

        const fileInput = imageForm.getFileInput(container);
        const addBtn = imageForm.getAddButton(container);
        const imageGrid = imageForm.getImageGrid(container);
        const dropHint = imageForm.getDropHint(container);

        if (!(fileInput instanceof HTMLInputElement)) return null;
        if (!(addBtn instanceof HTMLButtonElement)) return null;
        if (!(imageGrid instanceof HTMLElement)) return null;

        handler.container = container;
        handler.fileInput = fileInput;
        handler.addBtn = addBtn;
        handler.imageGrid = imageGrid;
        handler.dropHint = dropHint instanceof HTMLElement ? dropHint : imageGrid;

        ImageSetup.applyStaticControlSymbols(handler.container);

        const existingImageTiles = imageForm.getExistingImageTiles(handler.container);
        if (existingImageTiles.length > 0) {
            handler.config.existingImages = Array.from(existingImageTiles).flatMap(
                (tile, index): ExistingImageData[] => {
                    if (!(tile instanceof HTMLElement)) {
                        return [];
                    }
                    const img = imageForm.getImage(tile);
                    if (!(img instanceof HTMLImageElement)) {
                        return [];
                    }
                    const imageId = toExistingImageId(tile.dataset.imageId, index);
                    return [
                        {
                            id: imageId,
                            url: img.src,
                            filename: tile.dataset.imageFilename || img.alt,
                        },
                    ];
                },
            );
        }

        ImageListener.setupEditModeClickListeners(handler);
        ImageListener.setupFileInputListener(handler);
        ImageListener.setupAddButtonListener(handler);
        registerImageHandler(postKey, handler);
        return handler;
    }
}

export { ImageSetup, getImageHandler, getImageHandlerByPostId, registerImageHandler };
